/**
 * Fire-and-forget click / view tracking.
 *
 * Design rules:
 *  - NEVER blocks the UI: mutations run in the background, results ignored.
 *  - NEVER surfaces errors: analytics failures are invisible to the user.
 *  - VIEWS are impressions: a view is counted the first time an entity's card
 *    becomes visible on screen (the /brands, /shops and /promotions grids and
 *    search results — the home page's cards are not impression-tracked) OR
 *    when its detail page opens. Deduped once per entity per session (in
 *    memory, so a reload starts a new one) so scrolling back and forth
 *    doesn't inflate the count.
 *  - VIEWS are BATCHED — see "View batching" below.
 *  - CLICKS are deduped with a short cooldown to absorb accidental
 *    double-clicks; repeat visits still count. Each click is its own request,
 *    sent straight away.
 *  - Skips invalid (non-ObjectId) ids — placeholder content is never tracked.
 *  - Skips crawlers and automated browsers (`isLikelyBot`): Googlebot and
 *    friends run our JavaScript, and without this a crawl of /brands would
 *    count an impression for every card and a view for every detail page.
 *
 * View batching
 * -------------
 * The public API's rate limiter counts every HTTP request (300 a minute per
 * IP). One request per impression let a single pass over /brands and /shops
 * (~285 cards) spend the whole budget, and the list queries sharing it then
 * failed with 429. So `trackView` only queues, and the queue goes out as one
 * `trackViews` mutation:
 *  - as soon as it holds CLIENT_MAX_BATCH views, or
 *  - FLUSH_DELAY_MS after the FIRST view was queued. Later views don't push
 *    that timer back, so no view waits much longer than that and a continuous
 *    scroll costs about one request per FLUSH_DELAY_MS;
 *  - on `pagehide` or the tab going hidden, straight away — see "Page exit".
 * One batch is in flight at a time. A batch that fails with HTTP 429, a 5xx,
 * or no response at all goes back to the front of the queue, and the next
 * send waits — for the 429 body's `retryAfter` when it has one, otherwise an
 * exponential backoff. A view is sent at most MAX_ATTEMPTS times, then
 * dropped. Any other failure drops the batch: another 4xx is rejected before
 * anything runs and would fail the same way again, and a GraphQL error is how
 * `trackViews` reports a write that failed part-way (HTTP 200, code '500'), so
 * some of the batch may already be counted and a retry could count it twice.
 * The exception is a backend that has no `trackViews` yet, see "Older backend".
 *
 * Page exit
 * ---------
 * No code runs once the page is gone, so every batch goes out with
 * `keepalive` (a batch is a few KB, far under the browser's 64 KB keepalive
 * budget): one still on the wire when the tab closes is not cancelled. On
 * `pagehide` or the tab going hidden, everything queued is sent at once,
 * ALONGSIDE a batch already in flight rather than after it — "after" would
 * never come. That is the one exception to one-in-flight, and it only happens
 * as the page leaves, so it can't raise the steady request rate. A running
 * retry backoff is still honoured: views it holds back are lost if the page
 * doesn't come back (best effort, like everything here).
 *
 * Older backend
 * -------------
 * The web app and the API deploy separately. A backend without `trackViews`
 * rejects every batch with HTTP 400, so rather than drop views until it
 * catches up, the client falls back to the single viewX mutations for the
 * rest of the session — one request per view, as before batching. A backend
 * that has `trackViews` never triggers it.
 */
import type { DocumentNode } from 'graphql';
import { getApolloClient } from '@/lib/apollo';
import { httpStatus, retryAfterSeconds } from '@/utils/validation/authErrors';
import { isLikelyBot } from '@/utils/bot';
import {
  TRACK_VIEWS,
  VIEW_BRAND,
  VIEW_STORE,
  VIEW_SHOP,
  VIEW_PROMOTION,
  CLICK_BRAND,
  CLICK_STORE,
  CLICK_SHOP,
  CLICK_PROMOTION,
} from '@/graphql/tracking';

export type TrackableEntity = 'brand' | 'store' | 'shop' | 'promotion';

/** Mongo ObjectIds are 24 hex chars — anything else is dummy data. */
const OBJECT_ID_RE = /^[a-f\d]{24}$/i;

/** The backend's `EntityType` enum, as `trackViews` takes it. */
type EntityType = 'BRAND' | 'STORE' | 'SHOP' | 'PROMOTION';

const ENTITY_TYPES: Record<TrackableEntity, EntityType> = {
  brand: 'BRAND',
  store: 'STORE',
  shop: 'SHOP',
  promotion: 'PROMOTION',
};

const CLICK_MUTATIONS: Record<TrackableEntity, DocumentNode> = {
  brand: CLICK_BRAND,
  store: CLICK_STORE,
  shop: CLICK_SHOP,
  promotion: CLICK_PROMOTION,
};

/** One view per request — only for a backend older than `trackViews`. */
const LEGACY_VIEW_MUTATIONS: Record<EntityType, DocumentNode> = {
  BRAND: VIEW_BRAND,
  STORE: VIEW_STORE,
  SHOP: VIEW_SHOP,
  PROMOTION: VIEW_PROMOTION,
};

/** Every batch may outlive the page — see "Page exit". */
const KEEPALIVE_CONTEXT = { fetchOptions: { keepalive: true } };

/** Views per `trackViews` request (the server accepts up to 100). */
export const CLIENT_MAX_BATCH = 50;
/** How long the first queued view waits for others before the queue is sent. */
export const FLUSH_DELAY_MS = 1500;
/** Sends per view — the first plus two retries — before it is dropped. */
export const MAX_ATTEMPTS = 3;
/** Backoff when a retryable failure carries no `retryAfter`: 2s, 4s, 8s… */
const BACKOFF_BASE_MS = 2000;
const BACKOFF_MAX_MS = 30_000;

interface QueuedView {
  entityType: EntityType;
  id: string;
  /** Sends of this view that have already failed. */
  attempts: number;
}

/** Entities whose impression (view) has already been counted this session. */
const viewedThisSession = new Set<string>();

/** Recent clicks (key → timestamp) to absorb accidental double-clicks. */
const recentClicks = new Map<string, number>();
const CLICK_COOLDOWN_MS = 1500;

/** Views waiting to be sent, oldest first. */
const queue: QueuedView[] = [];
/** The next scheduled flush. Armed once and never pushed back by later views. */
let flushTimer: ReturnType<typeof setTimeout> | null = null;
/** The batch currently on the wire (with any page-exit batches), if any. */
let inFlight: Promise<void> | null = null;
/** A flush was due while a batch was in flight — send as soon as it settles. */
let flushRequested = false;
/** No send before this time (ms since epoch); set by a retryable failure. */
let blockedUntil = 0;
/** Retryable failures in a row, for the exponential backoff. */
let consecutiveFailures = 0;
/** Bumped by the reset hook so a send that settles afterwards changes nothing. */
let generation = 0;
/** The server has no `trackViews`: views go one per request, unbatched. */
let legacyViews = false;
let listeningForPageExit = false;

function isTrackable(id: string | undefined | null): id is string {
  return typeof id === 'string' && OBJECT_ID_RE.test(id);
}

function fire(mutation: DocumentNode, id: string): void {
  getApolloClient()
    .mutate({ mutation, variables: { id }, fetchPolicy: 'no-cache' })
    .catch(() => {
      /* analytics must never throw — swallow everything */
    });
}

function armFlushTimer(delayMs: number): void {
  if (flushTimer !== null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushViews();
  }, delayMs);
}

function clearFlushTimer(): void {
  if (flushTimer === null) return;
  clearTimeout(flushTimer);
  flushTimer = null;
}

function pageHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

function onPageHide(): void {
  flushForPageExit();
}

function onVisibilityChange(): void {
  if (pageHidden()) flushForPageExit();
}

/** Installed on the first queued view, so importing this module has no side effects. */
function listenForPageExit(): void {
  if (listeningForPageExit || typeof window === 'undefined') return;
  listeningForPageExit = true;
  window.addEventListener('pagehide', onPageHide);
  document.addEventListener('visibilitychange', onVisibilityChange);
}

/**
 * Worth sending again: HTTP 429, a 5xx, or a request that got no response at
 * all (offline, DNS, CORS). A GraphQL-level error has no `networkError`.
 */
function isRetryable(err: unknown): boolean {
  const status = httpStatus(err);
  if (status === undefined) {
    return Boolean((err as { networkError?: unknown } | null)?.networkError);
  }
  return status === 429 || status >= 500;
}

/**
 * The 429 body's `retryAfter` when it has one (Apollo leaves `graphQLErrors`
 * empty for the limiter's 429, and its Retry-After header is not CORS-exposed,
 * so `retryAfterSeconds` reads it from `networkError.result`); otherwise an
 * exponential backoff.
 */
function retryDelayMs(err: unknown): number {
  const retryAfter = retryAfterSeconds(err);
  if (retryAfter !== undefined) return retryAfter * 1000;
  return Math.min(BACKOFF_BASE_MS * 2 ** (consecutiveFailures - 1), BACKOFF_MAX_MS);
}

/**
 * The server rejected the document because it has no `trackViews` — a backend
 * older than this client. Apollo Server answers HTTP 400 and one of its errors
 * names the missing field; not necessarily the first, since the unknown
 * `TrackViewInput` type is reported before it.
 */
function serverLacksTrackViews(err: unknown): boolean {
  const anyErr = err as {
    graphQLErrors?: unknown;
    networkError?: { result?: { errors?: unknown } } | null;
  } | null;
  const errors = [anyErr?.graphQLErrors, anyErr?.networkError?.result?.errors]
    .filter(Array.isArray)
    .flat() as Array<{ message?: unknown } | null>;
  return errors.some(
    (e) => typeof e?.message === 'string' && e.message.startsWith('Cannot query field "trackViews"'),
  );
}

function sendLegacy(views: QueuedView[]): void {
  for (const { entityType, id } of views) fire(LEGACY_VIEW_MUTATIONS[entityType], id);
}

function onSendFailed(batch: QueuedView[], err: unknown): void {
  if (serverLacksTrackViews(err)) {
    // Deployed ahead of the backend: fall back rather than lose every view.
    legacyViews = true;
    clearFlushTimer();
    sendLegacy([...batch, ...queue.splice(0)]);
    return;
  }
  if (!isRetryable(err)) return; // dropped
  const again = batch
    .map((view) => ({ ...view, attempts: view.attempts + 1 }))
    .filter((view) => view.attempts < MAX_ATTEMPTS);
  queue.unshift(...again);
  consecutiveFailures += 1;
  const delay = retryDelayMs(err);
  blockedUntil = Date.now() + delay;
  // Replace any sooner timer: nothing goes out before the backoff ends.
  clearFlushTimer();
  if (queue.length > 0) armFlushTimer(delay);
}

async function send(batch: QueuedView[]): Promise<void> {
  const sentIn = generation;
  try {
    await getApolloClient().mutate({
      mutation: TRACK_VIEWS,
      variables: { views: batch.map(({ entityType, id }) => ({ entityType, id })) },
      fetchPolicy: 'no-cache',
      context: KEEPALIVE_CONTEXT,
    });
    if (sentIn === generation) consecutiveFailures = 0;
  } catch (err) {
    /* analytics must never throw — a failure is re-queued, dropped, or
       (older backend) resent one view per request */
    if (sentIn === generation) onSendFailed(batch, err);
  }
}

function afterSend(): void {
  const requested = flushRequested;
  flushRequested = false;
  if (queue.length === 0) return;
  if (requested || queue.length >= CLIENT_MAX_BATCH) {
    void flushViews(); // a running backoff turns this into a timer
  } else {
    armFlushTimer(FLUSH_DELAY_MS);
  }
}

/**
 * Send `batches` and make them the flush in flight. A batch that was already
 * in flight (the page-exit case) is waited for too, so nothing else goes out
 * until every one of them has settled.
 */
function startSending(batches: QueuedView[][]): Promise<void> {
  const sends = batches.map((batch) => send(batch));
  if (inFlight) sends.push(inFlight);
  const sending = Promise.all(sends).then(() => {
    // Joined by a page-exit send, or reset: whoever replaced it carries on.
    if (inFlight !== sending) return;
    inFlight = null;
    afterSend();
  });
  inFlight = sending;
  return sending;
}

/**
 * Send the queued views now as one `trackViews` request — at most
 * CLIENT_MAX_BATCH of them; the rest follow once it settles. Never rejects.
 *
 * During a retry backoff this only makes sure the backoff timer is set. While
 * a batch is in flight it only notes the request; that batch settling sends
 * the rest.
 */
export function flushViews(): Promise<void> {
  if (inFlight) {
    if (queue.length > 0) flushRequested = true;
    return inFlight;
  }
  if (queue.length === 0) {
    clearFlushTimer();
    return Promise.resolve();
  }
  const wait = blockedUntil - Date.now();
  if (wait > 0) {
    armFlushTimer(wait);
    return Promise.resolve();
  }

  clearFlushTimer();
  return startSending([queue.splice(0, CLIENT_MAX_BATCH)]);
}

/**
 * `pagehide` or the tab going hidden: send everything queued now, in batches
 * of CLIENT_MAX_BATCH, even past a batch in flight — see "Page exit". A
 * running backoff keeps its timer and sends nothing.
 */
function flushForPageExit(): void {
  if (queue.length === 0 || blockedUntil > Date.now()) return;
  clearFlushTimer();
  flushRequested = false;
  const batches: QueuedView[][] = [];
  while (queue.length > 0) batches.push(queue.splice(0, CLIENT_MAX_BATCH));
  void startSending(batches);
}

/**
 * Count an impression "view" — the entity's card became visible on screen, or
 * its detail page opened. Deduped once per entity per session so a card
 * scrolling in and out of view (or being revisited) doesn't inflate the count.
 * The view is queued, not sent: see "View batching" above.
 */
export function trackView(entity: TrackableEntity, id: string | undefined | null): void {
  if (!isTrackable(id) || isLikelyBot()) return;
  const key = `${entity}:${id}`;
  if (viewedThisSession.has(key)) return;
  viewedThisSession.add(key);
  if (legacyViews) {
    fire(LEGACY_VIEW_MUTATIONS[ENTITY_TYPES[entity]], id);
    return;
  }
  queue.push({ entityType: ENTITY_TYPES[entity], id, attempts: 0 });
  listenForPageExit();
  if (queue.length >= CLIENT_MAX_BATCH) {
    void flushViews();
  } else {
    armFlushTimer(FLUSH_DELAY_MS);
  }
}

/**
 * Count a "click" — the user clicked through to the entity (card, marker,
 * carousel slide…). Cooldown absorbs double-clicks; repeat visits still count.
 */
export function trackClick(entity: TrackableEntity, id: string | undefined | null): void {
  if (!isTrackable(id) || isLikelyBot()) return;
  const key = `${entity}:${id}`;
  const now = Date.now();
  const last = recentClicks.get(key);
  if (last && now - last < CLICK_COOLDOWN_MS) return;
  recentClicks.set(key, now);
  fire(CLICK_MUTATIONS[entity], id);
}

/**
 * Test/diagnostic hook — resets session dedupe state and the view queue: queued
 * views, the flush timer, any backoff, the legacy fallback, and the in-flight
 * marker (a send still on the wire settles without touching the new state).
 */
export function __resetTrackingState(): void {
  viewedThisSession.clear();
  recentClicks.clear();
  queue.length = 0;
  clearFlushTimer();
  inFlight = null;
  flushRequested = false;
  blockedUntil = 0;
  consecutiveFailures = 0;
  legacyViews = false;
  generation += 1;
}
